import React, { useCallback } from 'react'
import * as Sentry from '@sentry/react'

export function useSentryBreadcrumb() {
  const addBreadcrumb = useCallback((
    message: string,
    data?: Record<string, any>,
    category: string = 'user_action',
    level: Sentry.SeverityLevel = 'info'
  ) => {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: Date.now() / 1000,
      data: {
        ...data,
        component: data?.component || 'unknown',
      }
    })
  }, [])

  const addClickBreadcrumb = useCallback((
    elementName: string,
    action: string,
    data?: Record<string, any>
  ) => {
    addBreadcrumb(
      `User clicked ${elementName}`,
      {
        element: elementName,
        action,
        ...data
      },
      'ui.click'
    )
  }, [addBreadcrumb])

  const addInputBreadcrumb = useCallback((
    fieldName: string,
    value?: string,
    data?: Record<string, any>
  ) => {
    addBreadcrumb(
      `User input in ${fieldName}`,
      {
        field: fieldName,
        hasValue: !!value,
        valueLength: value?.length || 0,
        ...data
      },
      'ui.input'
    )
  }, [addBreadcrumb])

  const addNavigationBreadcrumb = useCallback((
    from: string,
    to: string,
    data?: Record<string, any>
  ) => {
    addBreadcrumb(
      `Navigated from ${from} to ${to}`,
      {
        from,
        to,
        ...data
      },
      'navigation'
    )
  }, [addBreadcrumb])

  const addApiCallBreadcrumb = useCallback((
    endpoint: string,
    method: string,
    status?: number,
    data?: Record<string, any>
  ) => {
    addBreadcrumb(
      `API ${method} ${endpoint}`,
      {
        endpoint,
        method,
        status,
        ...data
      },
      'api',
      status && status >= 400 ? 'error' : 'info'
    )
  }, [addBreadcrumb])

  return {
    addBreadcrumb,
    addClickBreadcrumb,
    addInputBreadcrumb,
    addNavigationBreadcrumb,
    addApiCallBreadcrumb,
  }
}

// Higher-order component to automatically track component lifecycle
export function withBreadcrumbs<P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) {
  return (props: P) => {
    const { addBreadcrumb } = useSentryBreadcrumb()

    // Track component mount
    React.useEffect(() => {
      addBreadcrumb(`${componentName} mounted`, {
        component: componentName
      }, 'component.lifecycle')

      return () => {
        addBreadcrumb(`${componentName} unmounted`, {
          component: componentName
        }, 'component.lifecycle')
      }
    }, [])

    return <Component {...props} />
  }
}